import React, { useState, useEffect } from 'react'
import { Spinner, Toggle } from 'components'
import { logger } from 'helpers/logger'
import { AnalyticsChartOrientation, FieldType } from 'app/types'
import { transformAnalyticsForChart } from 'helpers/chartTransformers'
import { AnalyticsLineChart } from './AnalyticsLineChart'
import { AnalyticsBarChart } from './AnalyticsBarChart'
import { AnalyticsPieChart } from './AnalyticsPieChart'
import { AnalyticsTopChart } from './AnalyticsTopChart'
import { AnalyticsFunnelChart } from './AnalyticsFunnelChart'
import { AnalyticsStackedFunnelChart } from './AnalyticsStackedFunnelChart'

interface AnalyticsChartViewProps {
    data: any[]
    definition: any
    maps?: any
    chartType?: string
    onChartTypeChange?: (chartType: string) => void
    loading?: boolean
    className?: string
    readOnly?: boolean
    transitioning?: boolean
    marginModifier?: number
    aggregation?: string
    onPointClick?: (payload: AnalyticsChartPointSelection) => void
}

export interface AnalyticsChartPointSelection {
    chartType: string
    labelKey: string
    labelValue: string | number
    row: any
}

export function AnalyticsChartView(props: AnalyticsChartViewProps) {
    const {
        data,
        definition,
        maps,
        chartType: initialChartType,
        onChartTypeChange,
        loading,
        readOnly,
        className = '',
        transitioning = false,
        aggregation,
        onPointClick,
    } = props
    const [localChartType, setLocalChartType] = useState<string>(initialChartType || null)

    const handleChangeChartType = (type: string) => {
        setLocalChartType(type)
        if (onChartTypeChange) {
            onChartTypeChange(type)
        }
    }

    // Sync local state with prop changes
    useEffect(() => {
        if (initialChartType !== undefined && initialChartType !== localChartType) {
            handleChangeChartType(initialChartType)
        }
    }, [initialChartType])

    // Extract config from definition
    const {
        category,
        label,
        fields = [],
        series = [], // Extract series from definition
    } = definition || {}

    // Function to transform labels using maps
    const transformLabelsWithMaps = (dataToTransform: any[]) => {
        if (!maps || !dataToTransform || dataToTransform.length === 0) {
            return dataToTransform
        }

        return dataToTransform.map((item) => {
            const transformedItem = { ...item }
            
            // Check each field in the item to see if it has a corresponding map
            Object.keys(item).forEach((key) => {
                const value = item[key]
                
                // Skip null/undefined values
                if (value == null) {
                    return
                }
                
                // Check if there's a map for this field
                if (maps[key] && maps[key][value]) {
                    // Transform the value using the map's label
                    transformedItem[key] = maps[key][value].label || value
                }
            })
            
            return transformedItem
        })
    }

    // Transform labels before chart transformation
    const dataWithTransformedLabels = transformLabelsWithMaps(data)

    // Determine available fields for chart aggregation
    const numericFields = fields
        .filter((f) => f.type === FieldType.Integer || f.type === FieldType.Float || f.type === FieldType.Currency)
        .map((f) => ({
            label: f.label,
            key: f.key,
            valueField: f.key,
        }))

    // Determine available fields for chart grouping
    const categoryFields = fields
        .filter((f) => !f.action 
            && !f.items 
            && !f.hidden
            && f.key !== 'dateVisited'
            && !numericFields.some((nf) => nf.key === f.key))
        .map((f) => ({
            label: f.label,
            key: f.key,
            labelField: f.key,
        }))

    // Default transformation options
    const [chartOptions, setChartOptions] = useState({
        valueField: numericFields.length > 0 ? numericFields[0].valueField : 'count',
        labelField: categoryFields.length > 0 ? categoryFields[0].labelField : 'name',
        dateField: 'date',
        aggregationType: label || category,
        category,
        limit: series.length > 0 ? 50 : 15, // Higher limit for combined series charts
        orientation: definition?.chartOrientation || AnalyticsChartOrientation.Vertical,
        excludeNullValues: definition?.excludeNullValues || false, // Pass through excludeNullValues option
    })

    // State to control the selected field mode (combined or individual field)
    const [selectedFieldMode, setSelectedFieldMode] = useState(
        series.length > 0 ? 'combined' : (numericFields.length > 0 ? numericFields[0].valueField : 'count')
    )

    // Determine if we're using combined series based on the selected mode
    const useCombinedSeries = selectedFieldMode === 'combined'

    // Transform the data for charting using the label-transformed data
    const transformedData = transformAnalyticsForChart(dataWithTransformedLabels, {
        ...chartOptions,
        chartType: localChartType,
        series: useCombinedSeries ? series : [], // Only pass series when combined mode is active
    })

    // Auto-detect chart type if not set explicitly
    const effectiveChartType = localChartType || transformedData.type || 'bar'

    const handleChangeFieldMode = (mode: string) => {
        setSelectedFieldMode(mode)
        
        // If it's not combined mode, update the valueField in chartOptions
        if (mode !== 'combined') {
            setChartOptions({
                ...chartOptions,
                valueField: mode,
            })
        }
    }

    const handleChangeValueField = (field: string) => {
        setChartOptions({
            ...chartOptions,
            valueField: field,
        })
    }

    const handleChangeCategoryField = (field: string) => {
        setChartOptions({
            ...chartOptions,
            labelField: field,
        })
    }

    const renderChart = () => {
        if (loading) {
            return <div className="chart-container"><Spinner /></div>
        }

        if (!data || data.length === 0) {
            return (
                <div className="chart-container">
                    <div className="no-chart-data">No data available for chart visualization</div>
                </div>
            )
        }

        // Calculate if toggles are present
        const hasValueFieldToggle = numericFields.length > 1
        const hasCategoryFieldToggle = categoryFields.length > 1
        const hasToggles = hasValueFieldToggle || hasCategoryFieldToggle

        try {
            switch (effectiveChartType) {
                case 'line':
                    return (
                        <AnalyticsLineChart
                            data={transformedData.chartData}
                            xKey={transformedData.dateKey}
                            dataKey={transformedData.dataKey}
                            series={useCombinedSeries ? series : undefined}
                            category={transformedData.category}
                            transitioning={transitioning}
                            isDuration={transformedData.isDuration}
                            durationScale={transformedData.durationScale}
                            hasToggles={hasToggles}
                            dateFormat={definition?.dateFormat}
                            autoDetectDateFormat={!definition?.dateFormat}
                            marginModifier={props.marginModifier}
                            onPointClick={onPointClick}
                        />
                    )

                case 'bar':
                    return (
                        <AnalyticsBarChart
                            data={transformedData.chartData}
                            dataKey={transformedData.dataKey}
                            labelKey={transformedData.labelKey}
                            transitioning={transitioning}
                            orientation={chartOptions.orientation}
                            isDuration={transformedData.isDuration}
                            durationScale={transformedData.durationScale}
                            hasToggles={hasToggles}
                            series={transformedData.series}
                            showPagination={true}
                            pageSize={10}
                            dateFormat={definition?.dateFormat}
                            autoDetectDateFormat={!definition?.dateFormat}
                            marginModifier={props.marginModifier}
                            onPointClick={onPointClick}
                        />
                    )

                case 'pie':
                    return (
                        <AnalyticsPieChart
                            data={transformedData.chartData}
                            labelKey={transformedData.labelKey}
                            valueKey={transformedData.valueKey}
                            transitioning={transitioning}
                            hasToggles={hasToggles}
                            marginModifier={props.marginModifier}
                            onPointClick={onPointClick}
                        />
                    )

                case 'top':
                    return (
                        <AnalyticsTopChart
                            data={transformedData.chartData}
                            dataKey={transformedData.dataKey}
                            labelKey={transformedData.labelKey}
                            transitioning={transitioning}
                            isDuration={transformedData.isDuration}
                            durationScale={transformedData.durationScale}
                            hasToggles={hasToggles}
                            series={transformedData.series}
                            title={transformedData.title}
                            limit={chartOptions.limit}
                            showPagination={true}
                            pageSize={10}
                            marginModifier={props.marginModifier}
                            onPointClick={onPointClick}
                        />
                    )

                case 'funnel': {
                    // Build ordered funnel steps from the first data row
                    const FUNNEL_STEPS = [
                        { key: 'visitCount', name: 'Visits', fill: '#3498db' },
                        { key: 'registrationCount', name: 'Registrations', fill: '#27ae60' },
                        { key: 'meetingCount', name: 'Meetings', fill: '#e67e22' },
                        { key: 'saleCount', name: 'Sales', fill: '#e74c3c' },
                    ]
                    const row = data && data.length > 0 ? data[0] : {}
                    const funnelData = FUNNEL_STEPS
                        .map((step) => ({
                            name: step.name,
                            value: Number(row[step.key] ?? 0),
                            fill: step.fill,
                        }))
                        .filter((step) => step.value > 0 || FUNNEL_STEPS.some((s) => s.name === step.name))
                    return (
                        <AnalyticsFunnelChart
                            data={funnelData}
                            transitioning={transitioning}
                            hasToggles={false}
                            marginModifier={props.marginModifier}
                            onPointClick={onPointClick}
                        />
                    )
                }

                case 'stacked-funnel': {
                    return (
                        <AnalyticsStackedFunnelChart
                            data={data}
                            transitioning={transitioning}
                            marginModifier={props.marginModifier}
                            aggregation={aggregation}
                            onPointClick={onPointClick}
                        />
                    )
                }

                default:
                    return (
                        <div className="chart-container">
                            <div className="no-chart-data">Unsupported chart type: {effectiveChartType}</div>
                        </div>
                    )
            }
        } catch (error) {
            logger.error('Error rendering chart:', error)
            return (
                <div className="chart-container">
                    <div className="no-chart-data">Error rendering chart. Please try a different chart type.</div>
                </div>
            )
        }
    }

    return (
        <div className={`analytics-chart-view ${className}`}>
            {!loading && effectiveChartType !== 'funnel' && effectiveChartType !== 'stacked-funnel' && <div className="chart-toggles">

                {/* Flattened Field Selector - show combined option with individual fields when series are available */}
                {(series.length > 0 || numericFields.length > 1) && (
                    <div className="control-group">
                        <Toggle
                            items={[
                                // Add Combined option if series are available
                                ...(series.length > 0 ? [{ value: 'combined', text: 'Combined' }] : []),
                                // Add individual numeric fields
                                ...numericFields.map((field) => ({
                                    value: field.valueField,
                                    text: field.label,
                                }))
                            ]}
                            value={selectedFieldMode}
                            onChange={handleChangeFieldMode}
                            toggleOff={false}
                            arrowFocus={false}
                            selectedClass="selected"
                        />
                    </div>
                )}

                {/* Category Field Selector - only show when not using combined series */}
                {!useCombinedSeries && categoryFields.length > 1 && (
                    <div className="control-group">
                        <Toggle
                            items={categoryFields.map((field) => ({
                                value: field.labelField,
                                text: field.label,
                            }))}
                            value={chartOptions.labelField}
                            onChange={handleChangeCategoryField}
                            toggleOff={false}
                            arrowFocus={false}
                            selectedClass="selected"
                        />
                    </div>
                )}
            </div>}
            {renderChart()}
        </div>
    )
}